import { Banner } from '@cloudflare/kumo';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAiStatus } from '../components/AiAssistant';
import { JevComposer } from '../components/jev/JevComposer';
import { JevConversation } from '../components/jev/JevConversation';
import { useJevChat } from '../components/jev/useJevChat';
import { ChannelBadge } from '../components/Inbox/ChannelBadge';
import { inboxApi } from '../services/api';
import { ICONS, glyph } from '../components/ui/icons';
import './AskAiPage.css';
import { SkeletonText } from '../components/ui/Skeleton';

interface Thread { id: string; title: string; updatedAt: string }
interface Waiting {
  id: string; channel: 'whatsapp' | 'instagram' | 'widget'; status: string; lastMessageAt: string;
  lastPreview: string | null; contact: { nome: string } | null;
}

const SUGGESTIONS = [
  { icon: 'Sparkle', text: 'Resuma as conversas de hoje' },
  { icon: 'Charts', text: 'Quais negócios fecham esta semana?' },
  { icon: 'Person', text: 'Quantos contatos novos chegaram nos últimos 7 dias?' },
] as const;

function greeting(now = new Date()) {
  const h = now.getHours();
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

const ago = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (m < 1) return 'agora';
  if (m < 60) return `há ${m} min`;
  const h = Math.round(m / 60);
  return h < 24 ? `há ${h} h` : new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

/**
 * Início: pergunte qualquer coisa à IA (Lumi) sobre a organização. A pergunta
 * abre o painel da Lumi numa conversa nova; abaixo, as conversas da caixa de
 * entrada que esperam alguém da equipe.
 */
export function AskAiPage() {
  const { user } = useAuth();
  const fetchApi = useAuthenticatedFetch();
  const navigate = useNavigate();
  const ai = useAiStatus();
  const chat = useJevChat();
  const [text, setText] = useState('');
  const [recent, setRecent] = useState<Thread | null>(null);
  const [waiting, setWaiting] = useState<Waiting[] | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const jev = !!ai?.available && ai?.jev !== false;
  const firstName = (user?.nome || '').trim().split(/\s+/)[0];

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    if (!jev) return;
    fetchApi('/api/jev/threads').then((r) => (r.ok ? r.json() : null)).then((d) => setRecent(d?.threads?.[0] ?? null)).catch(() => undefined);
  }, [fetchApi, jev]);
  useEffect(() => {
    inboxApi.list({ view: 'queue' })
      .then((d) => setWaiting((d.conversations as Waiting[]).slice(0, 6)))
      .catch(() => setWaiting([]));
  }, []);

  const ask = (q: string) => {
    const t = q.trim();
    if (!t || !jev) return;
    setText('');
    chat.send(t, true);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(text); }
  };

  // Como no Notion AI: ao enviar, a mesma tela vira a conversa (caixa desce para o rodapé)
  if (chat.items.length > 0 || chat.busy) {
    const title = chat.items.find((i) => i.kind === 'user')?.text.split('\n')[0] ?? 'Conversa';
    return (
      <div className="ask-chat" data-testid="ask-ai-chat">
        <header className="ask-chat__head">
          <ICONS.Sparkle size={16} />
          <span className="ask-chat__title">{title}</span>
        </header>
        <div className="ask-chat__body" aria-live="polite">
          <div className="ask-chat__col"><JevConversation chat={chat} avatars /></div>
        </div>
        <div className="ask-chat__foot">
          <button type="button" className="ask-clear" onClick={chat.newChat} data-testid="ask-ai-new" disabled={chat.busy}>
            <ICONS.Trash size={14} /> Limpar conversa
          </button>
          <JevComposer chat={chat} className="jev-panel__input ask-composer" placeholder="Ask Lumi" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-content ask-page" data-testid="ask-ai-page">
      <section className="ask-card">
        <h1 className="ask-greeting">{greeting()}{firstName ? `, ${firstName}` : ''}.</h1>

        {!jev && ai && (
          <Banner variant="default" description={<>A IA ainda não está disponível nesta organização. Fale com quem administra a plataforma para ligar o assistente.</>} />
        )}

        <div className="ask-box-wrap">
          {recent && (
            <button type="button" className="ask-recent" onClick={() => chat.loadThread(recent.id)} data-testid="ask-ai-recent">
              <ICONS.Clock size={16} />
              <span className="ask-recent__label">Conversa recente</span>
              <span className="ask-recent__title">{recent.title}</span>
            </button>
          )}
          <form className="ask-box" onSubmit={(e) => { e.preventDefault(); ask(text); }}>
            <textarea
              ref={inputRef}
              className="ask-box__input"
              placeholder="Pergunte qualquer coisa sobre suas conversas, contatos e vendas…"
              aria-label="Pergunte à IA"
              rows={3}
              maxLength={2000}
              value={text}
              disabled={!jev}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKey}
              data-testid="ask-ai-input"
            />
            <div className="ask-box__bar">
              <span className="ask-box__hint">Enter envia · Shift+Enter quebra a linha</span>
              <button type="submit" className="ask-box__send" aria-label="Enviar pergunta" disabled={!jev || !text.trim()} data-testid="ask-ai-send">
                <ICONS.ArrowUp size={16} />
              </button>
            </div>
          </form>
        </div>

        <div className="ask-chips">
          {SUGGESTIONS.map((s) => (
            <button key={s.text} type="button" className="ask-chip" disabled={!jev} onClick={() => ask(s.text)}>
              <span className="ask-chip__icon">{glyph(s.icon, 16)}</span>
              {s.text}
            </button>
          ))}
        </div>

        <div className="ask-list">
          <div className="ask-list__head">
            <span>Aguardando a equipe</span>
            <button type="button" className="ask-list__link" onClick={() => navigate('/inbox')}>Abrir caixa de entrada <ICONS.ChevronRight size={16} /></button>
          </div>
          {waiting === null && <SkeletonText lines={2} label="Carregando" />}
          {waiting?.length === 0 && <div className="ask-muted">Nenhuma conversa esperando. A IA está dando conta.</div>}
          <ul>
            {waiting?.map((c) => (
              <li key={c.id}>
                <button type="button" className="ask-row" onClick={() => navigate('/inbox')}>
                  <ChannelBadge channel={c.channel} dense />
                  <span className="ask-row__name">{c.contact?.nome ?? 'Visitante sem nome'}</span>
                  <span className="ask-row__preview">{c.lastPreview ?? ''}</span>
                  <span className="ask-row__time">{ago(c.lastMessageAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
